import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/ux/utils';

import styles from './ContentViewerNavigation.module.css';

interface ContentSegment {
  id: string;
  label: string;
  element: Element;
  offsetTop: number;
  height: number;
}

interface ContentViewerNavigationProps {
  containerRef?: React.RefObject<HTMLElement>;
  contentRef: React.RefObject<HTMLElement>;
  className?: string;
  useViewport?: boolean; // If true, use window scroll instead of container
}

export const ContentViewerNavigation: React.FC<ContentViewerNavigationProps> = ({
  containerRef,
  contentRef,
  className,
  useViewport = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [segments, setSegments] = useState<ContentSegment[]>([]);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const navigationRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>();

  // Calculate content segments based on headings and major content blocks
  const calculateSegments = useCallback(() => {
    if (!contentRef.current) return;
    if (!useViewport && !containerRef?.current) return;

    const content = contentRef.current;
    const container = useViewport ? window : containerRef?.current;

    // Check if content is scrollable
    const scrollable = useViewport
      ? document.documentElement.scrollHeight > window.innerHeight
      : container && container.scrollHeight > container.clientHeight;

    setIsScrollable(scrollable);

    if (!scrollable) return;

    // Find all major content sections (headings, images, videos)
    const sectionElements = content.querySelectorAll(
      'h1, h2, h3, h4, h5, h6, .video-proxy-container, .refreshable-image-container, .link-preview-decoration',
    );

    // If no sections found, create segments based on paragraphs
    if (sectionElements.length === 0) {
      const paragraphs = content.querySelectorAll('p');
      if (paragraphs.length > 0) {
        // Create segments at regular intervals through the content
        const totalHeight = useViewport
          ? document.documentElement.scrollHeight
          : containerRef!.current!.scrollHeight;

        // Create 3-5 segments for navigation
        const segmentCount = Math.min(5, Math.max(3, Math.floor(paragraphs.length / 3)));
        const tempSegments = [];

        for (let i = 0; i < segmentCount; i++) {
          const progress = i / (segmentCount - 1);
          const offsetTop = totalHeight * progress;

          tempSegments.push({
            id: `segment-${i}`,
            label: `Section ${i + 1}`,
            element: paragraphs[0], // Use first paragraph as reference
            offsetTop,
            height: totalHeight / segmentCount,
          });
        }

        setSegments(tempSegments);
        return;
      }
    }

    const newSegments: ContentSegment[] = [];

    // Process each section
    sectionElements.forEach((element, index) => {
      const rect = element.getBoundingClientRect();
      let offsetTop: number;

      if (useViewport) {
        // For viewport scrolling, use element's offset from document top
        offsetTop = window.pageYOffset + rect.top;
      } else {
        // For container scrolling
        const container = containerRef!.current!;
        const containerRect = container.getBoundingClientRect();
        offsetTop = rect.top - containerRect.top + container.scrollTop;
      }

      // Determine label based on element type
      let label = '';
      if (element.tagName.match(/^H[1-6]$/)) {
        label = element.textContent?.trim() || `Section ${index + 1}`;
        // Don't truncate - let CSS handle overflow with ellipsis
      } else if (element.classList.contains('video-proxy-container')) {
        const title = element.getAttribute('data-title');
        label = title ? `Video: ${title}` : 'Video Content';
      } else if (element.classList.contains('refreshable-image-container')) {
        const img = element.querySelector('img');
        const alt = img?.getAttribute('alt');
        label = alt ? `Image: ${alt}` : 'Image';
      } else if (element.classList.contains('link-preview-decoration')) {
        const href = element.getAttribute('data-href');
        if (href) {
          try {
            const url = new URL(href);
            label = `Link: ${url.hostname}`;
          } catch {
            label = 'Link Preview';
          }
        } else {
          label = 'Link Preview';
        }
      }

      newSegments.push({
        id: `segment-${index}`,
        label,
        element,
        offsetTop,
        height: rect.height,
      });
    });

    setSegments(newSegments);
  }, [containerRef, contentRef, useViewport]);

  // Handle scroll and calculate progress
  const handleScroll = useCallback(() => {
    if (segments.length === 0) return;
    if (!useViewport && !containerRef?.current) return;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      let scrollTop: number;
      let scrollHeight: number;
      let clientHeight: number;

      if (useViewport) {
        // Use window scroll values
        scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        scrollHeight = document.documentElement.scrollHeight;
        clientHeight = window.innerHeight;
      } else {
        // Use container scroll values
        const container = containerRef!.current;
        if (!container) return;
        scrollTop = container.scrollTop;
        scrollHeight = container.scrollHeight;
        clientHeight = container.clientHeight;
      }

      const maxScroll = scrollHeight - clientHeight;

      // Calculate overall progress
      const progress = maxScroll > 0 ? (scrollTop / maxScroll) * 100 : 0;
      setScrollProgress(progress);

      // Find active segment based on scroll position
      const viewportMiddle = scrollTop + clientHeight / 2;
      let activeIndex = 0;

      for (let i = segments.length - 1; i >= 0; i--) {
        if (segments[i].offsetTop <= viewportMiddle) {
          activeIndex = i;
          break;
        }
      }

      setActiveSegmentIndex(activeIndex);
    });
  }, [containerRef, segments, useViewport]);

  // Smooth scroll to position
  const scrollToPosition = useCallback(
    (position: 'top' | 'bottom') => {
      if (!useViewport && !containerRef?.current) return;

      if (useViewport) {
        const targetScroll =
          position === 'top' ? 0 : document.documentElement.scrollHeight - window.innerHeight;
        window.scrollTo({
          top: targetScroll,
          behavior: 'smooth',
        });
      } else {
        const container = containerRef!.current;
        const targetScroll = position === 'top' ? 0 : container.scrollHeight - container.clientHeight;
        container.scrollTo({
          top: targetScroll,
          behavior: 'smooth',
        });
      }
    },
    [containerRef, useViewport],
  );

  // Scroll to segment
  const scrollToSegment = useCallback(
    (segment: ContentSegment) => {
      if (!useViewport && !containerRef?.current) return;

      const targetScroll = Math.max(0, segment.offsetTop - 100); // 100px offset for better positioning

      if (useViewport) {
        window.scrollTo({
          top: targetScroll,
          behavior: 'smooth',
        });
      } else {
        const container = containerRef!.current;
        container.scrollTo({
          top: targetScroll,
          behavior: 'smooth',
        });
      }
    },
    [containerRef, useViewport],
  );

  // Remove hover detection since we want it always visible
  // The component will always be visible with 0.5 opacity, 1 on hover

  // Remove wheel event handler - let normal scrolling work

  // Initialize and handle resize
  useEffect(() => {
    calculateSegments();
    handleScroll();

    // Throttled scroll handler
    let scrollTicking = false;
    const throttledScrollHandler = () => {
      if (!scrollTicking) {
        requestAnimationFrame(() => {
          handleScroll();
          scrollTicking = false;
        });
        scrollTicking = true;
      }
    };

    // Add scroll listener to window or container
    if (useViewport) {
      window.addEventListener('scroll', throttledScrollHandler, { passive: true });
    } else if (containerRef?.current) {
      containerRef.current.addEventListener('scroll', throttledScrollHandler, { passive: true });
    }

    // Resize observer for content changes
    const resizeObserver = new ResizeObserver(() => {
      calculateSegments();
      handleScroll();
    });

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    // Mutation observer for dynamic content changes
    const mutationObserver = new MutationObserver(() => {
      calculateSegments();
    });

    if (contentRef.current) {
      mutationObserver.observe(contentRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      if (useViewport) {
        window.removeEventListener('scroll', throttledScrollHandler);
      } else if (containerRef?.current) {
        containerRef.current.removeEventListener('scroll', throttledScrollHandler);
      }
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [containerRef, contentRef, useViewport, segments.length]); // Only re-run when these truly change

  // Show navigation if content is scrollable, even without segments
  if (!isScrollable) return null;

  const totalHeight = useViewport
    ? document.documentElement.scrollHeight
    : containerRef?.current?.scrollHeight || 1;

  const navigationElement = (
    <div
      ref={navigationRef}
      className={cn(styles.navigation, styles.alwaysVisible, isHovering && styles.hovering, className)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      role="navigation"
      aria-label="Content navigation"
    >
      {/* Top navigation square */}
      <div className={styles.navSquare}>
        <button
          className={cn(styles.navSquareTop)}
          onClick={() => scrollToPosition('top')}
          aria-label="Scroll to top"
          style={{
            width: '100%',
            height: '100%',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M2 5L4 3L6 5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className={styles.navSquareTooltip}>Go to top</div>
      </div>

      {/* Vertical navigation line */}
      <div className={styles.navLineContainer}>
        <div className={styles.navLine}>
          {/* Background track */}
          <div className={styles.trackBackground} />

          {/* Segmented sections with proportional heights */}
          {segments.map((segment, index) => {
            const nextSegment = segments[index + 1];
            const segmentEnd = nextSegment ? nextSegment.offsetTop : totalHeight;
            const segmentHeight = segmentEnd - segment.offsetTop;
            const segmentHeightPercent = (segmentHeight / totalHeight) * 100;
            const segmentTopPercent = (segment.offsetTop / totalHeight) * 100;

            const isActive = index === activeSegmentIndex;
            const isPassed = scrollProgress > segmentTopPercent + segmentHeightPercent / 2;

            return (
              <button
                key={segment.id}
                className={cn(
                  styles.segment,
                  isActive && styles.segmentActive,
                  isPassed && styles.segmentPassed,
                )}
                style={{
                  top: `${segmentTopPercent}%`,
                  height: `${segmentHeightPercent}%`,
                }}
                onClick={() => scrollToSegment(segment)}
                aria-label={`Navigate to ${segment.label}`}
              >
                {/* Segment indicator */}
                <div className={styles.segmentIndicator} />

                {/* Custom tooltip */}
                <div className={styles.segmentTooltip}>{segment.label}</div>
              </button>
            );
          })}

          {/* Progress fill */}
          <div className={styles.progressFill} style={{ height: `${scrollProgress}%` }} />

          {/* Active position indicator */}
          <div className={styles.activeIndicator} style={{ top: `${scrollProgress}%` }} />
        </div>
      </div>

      {/* Bottom navigation square */}
      <div className={styles.navSquare}>
        <button
          className={cn(styles.navSquareBottom)}
          onClick={() => scrollToPosition('bottom')}
          aria-label="Scroll to bottom"
          style={{
            width: '100%',
            height: '100%',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M2 3L4 5L6 3"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className={styles.navSquareTooltip}>Go to bottom</div>
      </div>
    </div>
  );

  // Use portal for viewport mode to ensure fixed positioning works correctly
  if (useViewport && typeof document !== 'undefined') {
    return createPortal(navigationElement, document.body);
  }

  return navigationElement;
};
